import React, { useState, useEffect, useRef } from 'react';
import { importEspnPool, importCbsPool, importYahooPool, PoolImportResult } from '../api/api';
import { Loader2, Check, ChevronDown, Trophy } from 'lucide-react';
import { cn } from '../lib/utils';
import { trackEvent, trackRedditEvent } from '../utils/analytics';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';

type Provider = 'espn' | 'cbs' | 'yahoo' | null;

interface ValidationState {
  provider: Provider;
  poolId: string | null;
  error: string | null;
}

interface OnboardingWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onImportSuccess: (poolName: string) => void;
  hasPool: boolean;
  onOpenAdvanced?: () => void;
}

const TOTAL_STEPS = 6;

const STEP_TITLES = [
  'Import Your Pool',
  'View the Standings',
  'Build and Save a Scenario',
  'Simulate the Tournament',
  'Rooting Interests',
  "You're All Set!",
];

const FUN_MESSAGES = [
  'Populating brackets...',
  'Herding llamas...',
  'Pumping up basketballs...',
  'Analyzing matchups...',
  'Counting seeds...',
  'Planting seeds...',
];

const TIMEOUT_MS = 180000;

function parsePoolUrl(url: string): ValidationState {
  const trimmed = url.trim();
  if (!trimmed) {
    return { provider: null, poolId: null, error: null };
  }

  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    return { provider: null, poolId: null, error: null };
  }

  const hostname = parsed.hostname.toLowerCase();

  if (hostname.includes('espn.com')) {
    const groupId = parsed.searchParams.get('id');
    if (groupId && groupId.length > 0) {
      return { provider: 'espn', poolId: groupId, error: null };
    }
    return { provider: 'espn', poolId: null, error: "Could not find a pool ID in this URL. Make sure you've selected a pool." };
  }

  if (hostname.includes('cbssports.com')) {
    const pathMatch = parsed.pathname.match(/\/pools\/([^/]+)/);
    if (pathMatch && pathMatch[1]) {
      return { provider: 'cbs', poolId: pathMatch[1], error: null };
    }
    return { provider: 'cbs', poolId: null, error: "Could not find a pool ID in this URL. Make sure you've selected a pool." };
  }

  if (hostname.includes('yahoo.com')) {
    const pathMatch = parsed.pathname.match(/\/group\/(\d+)/);
    if (pathMatch && pathMatch[1]) {
      return { provider: 'yahoo', poolId: pathMatch[1], error: null };
    }
    return { provider: 'yahoo', poolId: null, error: "Could not find a group ID in this URL. Make sure you've selected a group." };
  }

  return { provider: null, poolId: null, error: null };
}

const OnboardingWizard: React.FC<OnboardingWizardProps> = ({
  isOpen,
  onClose,
  onImportSuccess,
  hasPool,
  onOpenAdvanced,
}) => {
  const [currentStep, setCurrentStep] = useState(0);

  // Import state (step 0)
  const [url, setUrl] = useState('');
  const [validation, setValidation] = useState<ValidationState>({ provider: null, poolId: null, error: null });
  const [isImporting, setIsImporting] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [importResult, setImportResult] = useState<PoolImportResult | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const messageIndexRef = useRef(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Track whether import happened during this wizard session
  const [importedDuringSession, setImportedDuringSession] = useState(false);

  // Accordion state for import details when pool already exists
  const [importAccordionOpen, setImportAccordionOpen] = useState(false);

  // Validate URL as user types
  useEffect(() => {
    setValidation(parsePoolUrl(url));
  }, [url]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(0);
      setUrl('');
      setValidation({ provider: null, poolId: null, error: null });
      setIsImporting(false);
      setStatusMessage('');
      setImportResult(null);
      setImportError(null);
      setImportedDuringSession(false);
      setImportAccordionOpen(false);
      messageIndexRef.current = 0;
    }
  }, [isOpen]);

  const startFunMessages = (provider: Provider) => {
    const providerName = provider === 'espn' ? 'ESPN' : provider === 'yahoo' ? 'Yahoo' : 'CBS';
    setStatusMessage(`Connecting to ${providerName}...`);
    messageIndexRef.current = 0;

    timerRef.current = setInterval(() => {
      setStatusMessage(FUN_MESSAGES[messageIndexRef.current % FUN_MESSAGES.length]);
      messageIndexRef.current++;
    }, 3000);
  };

  const stopTimers = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const handleImport = async () => {
    if (!validation.provider || !validation.poolId) return;

    setIsImporting(true);
    setImportResult(null);
    setImportError(null);
    startFunMessages(validation.provider);

    timeoutRef.current = setTimeout(() => {
      stopTimers();
      setIsImporting(false);
      setImportError('Import timed out. The pool may be too large or the service may be unavailable. Please try again.');
    }, TIMEOUT_MS);

    try {
      let result: PoolImportResult;
      if (validation.provider === 'espn') {
        result = await importEspnPool(validation.poolId);
      } else if (validation.provider === 'yahoo') {
        result = await importYahooPool(validation.poolId);
      } else {
        result = await importCbsPool(validation.poolId);
      }

      stopTimers();
      setIsImporting(false);

      if (result.success) {
        setImportResult(result);
        setImportedDuringSession(true);
        trackEvent('pool_import', { provider: validation.provider! });
        trackRedditEvent('Lead');
        onImportSuccess(result.pool_id);
      } else {
        setImportError('Import failed. Please check the URL and try again.');
      }
    } catch (err: any) {
      stopTimers();
      setIsImporting(false);
      setImportError(err.message || 'An unexpected error occurred.');
    }
  };

  const handleNext = () => {
    if (currentStep < TOTAL_STEPS - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleFinish = () => {
    localStorage.setItem('hasCompletedOnboarding', 'true');
    onClose();
  };

  const poolAvailable = hasPool || importedDuringSession;
  const isValid = validation.provider !== null && validation.poolId !== null;

  const renderImportForm = () => (
    <>
      <p className="text-[0.95rem] leading-relaxed text-foreground mb-3">
        Start by importing your bracket pool. Navigate to{' '}
        <a href="https://fantasy.espn.com/games/tournament-challenge-bracket-2026/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">ESPN</a>
        ,{' '}
        <a href="https://picks.cbssports.com/college-basketball/ncaa-tournament/bracket/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">CBS</a>
        , or{' '}
        <a href="https://tournament.fantasysports.yahoo.com/mens-basketball-bracket/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Yahoo</a>
        , select your pool, and paste the URL below. We'll pull in everyone's brackets automatically.
      </p>

      <Input
        type="text"
        placeholder="Paste your ESPN, CBS, or Yahoo pool URL here"
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        disabled={isImporting || !!importResult}
      />

      <div className="min-h-[24px] mt-2 mb-4">
        {!isImporting && !importResult && !importError && (
          <>
            {validation.error ? (
              <span className="text-sm text-destructive">{validation.error}</span>
            ) : validation.provider && validation.poolId ? (
              <span className="text-sm text-success font-medium">
                <Check className="inline h-4 w-4 mr-1" />
                {validation.provider === 'espn' ? 'ESPN' : validation.provider === 'yahoo' ? 'Yahoo' : 'CBS'} pool detected
              </span>
            ) : (
              <span className="text-sm text-muted-foreground">Paste an ESPN, CBS, or Yahoo pool URL to get started</span>
            )}
          </>
        )}
      </div>

      {!importResult && !importError && (
        <Button
          className="w-full"
          onClick={handleImport}
          disabled={!isValid || isImporting}
        >
          Add My Pool
        </Button>
      )}

      {isImporting && (
        <div className="mt-5 flex justify-center">
          <div className="text-center">
            <div className="flex items-center justify-center gap-3">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
              <span className="text-sm text-muted-foreground">{statusMessage}</span>
            </div>
            {validation.provider === 'cbs' && (
              <p className="text-xs text-muted-foreground mt-2">
                Importing brackets now. For very big pools, this might take 10+ minutes — it will continue to work in the background if you close this page.
              </p>
            )}
          </div>
        </div>
      )}

      {importResult && (
        <div className="mt-5 rounded-md border border-success/50 bg-success/10 p-4 flex gap-3 items-start">
          <Check className="h-5 w-5 text-success mt-0.5 shrink-0" />
          <div>
            <div className="font-bold mb-1">{importResult.pool_id}</div>
            <div className="text-sm">{importResult.entries_imported} bracket{importResult.entries_imported !== 1 ? 's' : ''} imported</div>
            {importResult.truncated && importResult.total_entries && (
              <div className="text-sm text-amber-600 dark:text-amber-400 mt-1.5">
                Pool has {importResult.total_entries} entries — only the first {importResult.entries_imported + importResult.entries_skipped} were imported.
              </div>
            )}
          </div>
        </div>
      )}

      {importError && !isImporting && (
        <div className="mt-5 rounded-md border border-destructive/50 bg-destructive/10 p-4 text-destructive text-sm">
          {importError}
        </div>
      )}

      <p className="text-sm text-muted-foreground mt-4 mb-0">
        You can also create a pool manually using the{' '}
        <button
          type="button"
          className="font-semibold text-primary hover:underline"
          onClick={onOpenAdvanced}
        >
          Advanced
        </button>{' '}
        option.
      </p>
    </>
  );

  const renderImportStep = () => (
    <div>
      {poolAvailable ? (
        <>
          <div className="rounded-md border border-primary/50 bg-primary/10 p-4 flex gap-3 items-center text-[0.95rem] text-foreground">
            <Check className="h-5 w-5 text-primary shrink-0" />
            <span>You already have a pool imported - nice! Click <strong>Next</strong> to continue the tour.</span>
          </div>
          <button
            className="flex items-center justify-between w-full py-3 mt-3 border-t border-border bg-transparent cursor-pointer text-[0.9rem] text-primary font-medium"
            onClick={() => setImportAccordionOpen(!importAccordionOpen)}
          >
            <span>Import another pool</span>
            <ChevronDown className={cn(
              "h-4 w-4 text-primary transition-transform duration-200",
              importAccordionOpen && "rotate-180"
            )} />
          </button>
          {importAccordionOpen && (
            <div className="pt-2">
              {renderImportForm()}
            </div>
          )}
        </>
      ) : (
        renderImportForm()
      )}
    </div>
  );

  const renderStandingsStep = () => (
    <div>
      <p className="text-[0.95rem] leading-relaxed text-foreground mb-3">
        The <strong>Generated Standings</strong> panel shows every player's score.
      </p>
      <p className="text-[0.95rem] leading-relaxed text-foreground mb-3">
        <strong>Current Score</strong> reflects points earned from games already decided.{' '}
        <strong>Scenario Score</strong> updates live as you build out a scenario in the bracket.
      </p>
      <p className="text-[0.95rem] leading-relaxed text-foreground mb-3">
        Click any <strong>player's name</strong> to load their picks into the scenario builder.
      </p>
      <img
        src="/onboarding/standings.png"
        alt="Generated standings showing player scores"
        className="w-full max-w-full rounded-lg border border-border mt-3"
      />
    </div>
  );

  const renderScenarioStep = () => (
    <div>
      <p className="text-[0.95rem] leading-relaxed text-foreground mb-3">
        Fill out the bracket to explore "what if" scenarios.
      </p>
      <p className="text-[0.95rem] leading-relaxed text-foreground mb-3">
        Click on team names in the Scenario Builder bracket to pick winners for remaining games.
      </p>
      <p className="text-[0.95rem] leading-relaxed text-foreground mb-3">
        Your scenario scores in the Generated Standings update in real time as you make picks.
      </p>
      <p className="text-[0.95rem] leading-relaxed text-foreground mb-3">
        Save scenarios you want to revisit. Use the <strong>Save Scenario</strong> button to save your current bracket as a named scenario, then switch between saved ones anytime.
      </p>
    </div>
  );

  const renderSimulateStep = () => (
    <div>
      <p className="text-[0.95rem] leading-relaxed text-foreground mb-3">
        Click <strong>Simulate Tournament</strong> to simulate all of the incomplete games from your scenario bracket.
        Each unknown game is simulated.
      </p>
      <p className="text-[0.95rem] leading-relaxed text-foreground mb-3">
        The results show each player's <strong>Probability</strong> of winning the pool. <strong>Paths</strong> shows you how many paths to victory each player has.
      </p>
      <img
        src="/onboarding/simulation-results.png"
        alt="Simulation results showing win probability for each player"
        className="w-full max-w-full rounded-lg border border-border mt-3"
      />
    </div>
  );

  const renderRootingStep = () => (
    <div>
      <p className="text-[0.95rem] leading-relaxed text-foreground mb-3">
        After simulating, click any player's name in the results to see their{' '}
        <strong>Rooting Interests</strong> - a breakdown of which upcoming games have the biggest impact on that player's chances.
      </p>
      <img
        src="/onboarding/rooting-interests.png"
        alt="Rooting interests showing which games matter most"
        className="w-full max-w-full rounded-lg border border-border mt-3"
      />
    </div>
  );

  const renderDoneStep = () => (
    <div className="text-center py-5">
      <Trophy className="h-12 w-12 mx-auto mb-4 text-primary" />
      <p className="text-[0.95rem] leading-relaxed text-foreground mb-3">
        You can always reopen this guide from the{' '}
        <strong className="text-primary">?</strong> button in the top right.
      </p>
    </div>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 0: return renderImportStep();
      case 1: return renderStandingsStep();
      case 2: return renderScenarioStep();
      case 3: return renderSimulateStep();
      case 4: return renderRootingStep();
      case 5: return renderDoneStep();
      default: return null;
    }
  };

  const isLastStep = currentStep === TOTAL_STEPS - 1;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open && !isImporting) { localStorage.setItem('hasCompletedOnboarding', 'true'); onClose(); } }}>
      <DialogContent className="sm:max-w-[640px] max-h-[85vh] flex flex-col p-0 gap-0"
        onInteractOutside={(e) => e.preventDefault()}
      >
        {/* Header */}
        <DialogHeader className="px-6 pt-5 pb-3">
          <DialogTitle className="text-[1.4rem] font-semibold">
            {STEP_TITLES[currentStep]}
          </DialogTitle>
          <DialogDescription>
            Step {currentStep + 1} of {TOTAL_STEPS}
          </DialogDescription>
        </DialogHeader>

        {/* Step dots */}
        <div className="flex justify-center gap-2 px-6 pb-4 border-b">
          {Array.from({ length: TOTAL_STEPS }, (_, i) => (
            <div
              key={i}
              className={cn(
                "w-2 h-2 rounded-full transition-colors duration-200",
                i === currentStep && "bg-primary scale-125",
                i < currentStep && "bg-primary",
                i > currentStep && "bg-muted-foreground/30"
              )}
            />
          ))}
        </div>

        {/* Body */}
        <ScrollArea className="flex-1 px-6 py-6">
          {renderStepContent()}
        </ScrollArea>

        {/* Footer */}
        <DialogFooter className="px-6 py-4 border-t flex-row justify-between sm:justify-between">
          {currentStep > 0 ? (
            <Button variant="outline" onClick={handleBack}>
              Back
            </Button>
          ) : (
            <div />
          )}
          {isLastStep ? (
            <Button onClick={handleFinish}>
              Finish
            </Button>
          ) : (
            <Button onClick={handleNext}>
              Next
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default OnboardingWizard;
